import React, { useState, useEffect } from 'react';
import { apiService } from '../../../services/api';

/**
 * Scheduled FOMC meetings, fetched from the backend, which scrapes the Fed's
 * own published calendar.
 *
 * Replaces a calendar that was typed into the component by hand. It listed the
 * 2025 meetings and labelled one of them "Next FOMC Meeting" - a claim that
 * stayed on the page for a year after that meeting had happened.
 */
const FomcCalendar = () => {
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const response = await apiService.getFomcCalendar();
        if (cancelled) return;

        const data = response?.data;
        if (!data || data.error || !Array.isArray(data.meetings) || data.meetings.length === 0) {
          setError(data?.error || 'The FOMC calendar is currently unavailable.');
        } else {
          setSchedule(data);
        }
      } catch (err) {
        if (!cancelled) setError(`Could not load the FOMC calendar: ${err.message}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, []);

  const currentYear = new Date().getFullYear();

  // Show this year's meetings, plus anything still upcoming beyond it.
  const visible = (schedule?.meetings || []).filter(
    (meeting) => meeting.year === currentYear || (!meeting.is_past && meeting.year > currentYear)
  );

  return (
    <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-semibold text-gray-900">📅 FOMC Meeting Schedule</h3>
        {schedule?.source && (
          <a
            href={schedule.source}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-blue-600 hover:underline"
          >
            Source: Federal Reserve →
          </a>
        )}
      </div>

      {loading && <p className="text-sm text-gray-500">Loading the published schedule…</p>}

      {!loading && error && (
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <p className="text-sm text-gray-600">{error}</p>
        </div>
      )}

      {!loading && !error && visible.length > 0 && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {visible.map((meeting) => {
              const isNext = schedule.next_meeting?.start_date === meeting.start_date;

              return (
                <div
                  key={meeting.start_date}
                  className={`rounded-lg border p-4 ${
                    isNext
                      ? 'bg-blue-50 border-blue-300 ring-1 ring-blue-300'
                      : meeting.is_past
                        ? 'bg-gray-50 border-gray-200'
                        : 'bg-white border-gray-200'
                  }`}
                >
                  <div className={`font-medium ${isNext ? 'text-blue-800' : 'text-gray-700'}`}>
                    {meeting.label}
                  </div>
                  <div className={`text-sm ${isNext ? 'text-blue-700' : 'text-gray-500'}`}>
                    {isNext ? 'Next meeting' : meeting.is_past ? 'Completed' : 'Scheduled'}
                  </div>
                  {meeting.has_projections && (
                    <div className="text-xs text-gray-500 mt-1">
                      Includes economic projections
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {schedule.next_meeting && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                <strong>Next meeting:</strong> {schedule.next_meeting.label}
                {schedule.next_meeting.has_projections &&
                  ', which includes an updated Summary of Economic Projections'}
                .
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FomcCalendar;
